import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLProps } from "react";
import Tick from "@/assets/icons/tick.svg?react";
import { Typography } from "@/components/ui/typography";
import { colors } from "@/constants/colors";
import { cn } from "@/lib/utils";

const checkboxContainerStyles = cva("relative h-5 w-5 rounded-[6px] border border-solid shrink-0", {
  variants: {
    disabled: {
      true: "",
    },
    checked: {
      true: "",
    },
  },
  compoundVariants: [
    {
      disabled: true,
      checked: true,
      className: "bg-gray-100 border-gray-300",
    },
    {
      disabled: true,
      checked: false,
      className: "bg-gray-100 border-gray-300",
    },
    {
      disabled: false,
      checked: false,
      className: "bg-white hover:bg-primary-100 hover:border-primary-600",
    },
    {
      disabled: false,
      checked: true,
      className: "bg-primary-50 border-primary-600 hover:bg-primary-100 hover:border-primary-600",
    },
  ],
  defaultVariants: {
    disabled: false,
    checked: false,
  },
});
interface CheckboxProps
  extends Omit<HTMLProps<HTMLInputElement>, "onChange" | "checked" | "disabled">,
    VariantProps<typeof checkboxContainerStyles> {
  className?: string;
  onChange: (value: boolean) => void;
  label?: string;
  bgChecked?: "black" | "green";
}

function Checkbox({ className, checked, disabled, label, onChange, ...other }: CheckboxProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <label className="flex items-center gap-2">
        <div className={checkboxContainerStyles({ checked, disabled })}>
          {checked && (
            <Tick
              stroke={disabled ? colors["gray-300"] : colors["primary-600"]}
              className={cn("absolute left-[4px] top-[4px] shrink-0")}
            />
          )}
          <input
            type="checkbox"
            className="h-full w-full appearance-none"
            checked={!!checked}
            disabled={!!disabled}
            onChange={(e) => {
              onChange(e.target.checked);
            }}
            {...other}
          />
        </div>
        <Typography variant="text-small">{label}</Typography>
      </label>
    </div>
  );
}

Checkbox.displayName = "Checkbox";

export { Checkbox };
